import { ContentItem } from '../shared/interfaces';

/** One maximal run of consecutive thinking items inside a message. */
export interface ThinkingRun {
  /** Index in `content` of the run's first item (where the group renders). */
  startIndex: number;
  /** Index in `content` of the run's last item (inclusive). */
  endIndex: number;
  /** The run's reasoning / tool_action / code_execution items, in order. */
  items: ContentItem[];
}

const GROUPABLE = new Set(['reasoning', 'tool_action', 'code_execution']);

/**
 * Find every maximal run of consecutive reasoning / tool_action /
 * code_execution items in a message (empty text items are transparent — they
 * neither join nor break a run). A run becomes a collapsed "Pensando…/Pensou"
 * group when it contains reasoning or 2+ steps; a single bare tool call stays
 * inline, and visible answer text is never absorbed — it separates runs.
 *
 * This keeps a turn with a pre-answer burst AND mid-answer bursts (narration
 * text between tool batches) down to one expandable line per burst instead of
 * a pile of boxes.
 */
export function computeThinkingRuns(content: ContentItem[]): ThinkingRun[] {
  const runs: ThinkingRun[] = [];
  let start = -1;
  let end = -1;
  let items: ContentItem[] = [];

  const flush = () => {
    if (items.length === 0) return;
    const hasReasoning = items.some((it: any) => it.type === 'reasoning');
    if (hasReasoning || items.length >= 2) {
      runs.push({ startIndex: start, endIndex: end, items });
    }
    start = -1;
    end = -1;
    items = [];
  };

  for (let i = 0; i < content.length; i++) {
    const it = content[i] as any;
    if (!it) {
      flush();
      continue;
    }
    if (it.type === 'text') {
      if (((it.text || '') as string).trim()) flush(); // visible text breaks the run
      continue; // empty text is transparent
    }
    if (GROUPABLE.has(it.type)) {
      if (items.length === 0) start = i;
      end = i;
      items.push(it);
      continue;
    }
    flush(); // file_ref / input_file / message_composer etc. break the run
  }
  flush();
  return runs;
}

/** Set of content indices swallowed by the given runs (skip in normal render). */
export function groupedIndexSet(runs: ThinkingRun[], content: ContentItem[]): Set<number> {
  const set = new Set<number>();
  for (const run of runs) {
    for (let i = run.startIndex; i <= run.endIndex; i++) {
      const it = content[i] as any;
      // Only the run's own items — a transparent empty-text item inside the
      // span keeps its (invisible) normal rendering.
      if (it && GROUPABLE.has(it.type)) set.add(i);
    }
  }
  return set;
}

/** True while any item of the run is still executing (streaming label state). */
export function thinkingBurstPending(items: ContentItem[]): boolean {
  return items.some((it: any) => !!it.pending);
}

/**
 * True when anything VISIBLE renders after ``index`` — non-empty text or any
 * non-text item. Invisible empty-text items don't count: a trailing thinking
 * run followed only by an empty text item is still "the end of the message",
 * and the group must keep its "Pensando" streaming state through the gap
 * between the draft finishing and the answer text starting.
 */
export function hasVisibleItemsAfter(content: ContentItem[], index: number): boolean {
  for (let i = index + 1; i < content.length; i++) {
    const it = content[i] as any;
    if (!it) continue;
    if (it.type === 'text') {
      if (((it.text || '') as string).trim()) return true;
      continue;
    }
    return true;
  }
  return false;
}

/**
 * True when the message ends in a thinking run with nothing visible after it —
 * i.e. while generation is active, the ThinkingGroup is the live activity
 * indicator ("Pensando"), so no extra spinner should render.
 */
export function trailingThinkingRunActive(content: ContentItem[]): boolean {
  const runs = computeThinkingRuns(content);
  if (runs.length === 0) return false;
  return !hasVisibleItemsAfter(content, runs[runs.length - 1].endIndex);
}
